import styled, { css } from 'styled-components';
import { ReactElement, useEffect, useState } from 'react';

import { toggleVisible } from 'styles/theme/animation';
import { useOnRouteChangeComplete } from 'shared/hooks/useOnRouteChangeComplete';
import { useOutsideClick } from 'shared/hooks/useOutsideClick';
import { useWindowSize } from 'shared/hooks/useWindowSize';

interface IHoverLinkMenu {
  align?: 'left' | 'center' | 'right';
  direction?: 'top' | 'bottom';
  dropdownMenu?: ReactElement;
  forceShowDropdownMenu?: boolean;
  isDropdownMenuPermanent?: boolean;
  menuItem: ReactElement;
  onDropdownMenuMouseEnter?: (e?: any) => void;
  onDropdownMenuMouseLeave?: () => void;
  style?: { [key: string]: string };
}

export const HoverDropdownMenu = ({
  align = 'center', // when aligning center, add `position: relative` to parent wrapper to center against
  direction = 'bottom',
  dropdownMenu,
  forceShowDropdownMenu = false,
  isDropdownMenuPermanent = false,
  menuItem,
  onDropdownMenuMouseEnter,
  onDropdownMenuMouseLeave,
  style,
}: IHoverLinkMenu) => {
  const { isMobile } = useWindowSize();
  const [showDropdownMenu, setShowDropdownMenu] = useState(false || forceShowDropdownMenu);

  const showDropdown = () => setShowDropdownMenu(true);
  const hideDropdown = () => setShowDropdownMenu(false);

  const handleClickOutside = () => {
    if (!isDropdownMenuPermanent) {
      hideDropdown();
    }
  };

  useOnRouteChangeComplete(hideDropdown);

  /*
   * mobile does not respect the hover functionality, so checking making sure
   * the menu is focused when open, and closed on outside click
   * */
  useOutsideClick(handleClickOutside);

  const handleMouseEnterDropdownMenu = () => {
    showDropdown();
    if (onDropdownMenuMouseEnter) {
      onDropdownMenuMouseEnter();
    }
  };

  const handleMouseLeaveDropdownMenu = () => {
    hideDropdown();
    if (onDropdownMenuMouseLeave) {
      onDropdownMenuMouseLeave();
    }
  };

  const handleMobileClick = () => {
    if (isMobile) {
      showDropdown();
    }
  };

  useEffect(() => {
    /*
     * mobile does not respect the hover functionality, so checking for clicks
     * on specific elements as well
     * */
    const handleButtonClick = (e) => {
      const clickedItem = e.target.getAttribute('data-qa');
      if (
        clickedItem === 'menu_close_button' ||
        clickedItem === 'log_in_button' ||
        clickedItem === 'create_account_button' ||
        clickedItem === 'log_out_button' ||
        clickedItem === 'size_preferences_button' ||
        clickedItem === 'location_preferences_button'
      ) {
        hideDropdown();
      }
    };
    window.addEventListener('click', (e) => handleButtonClick(e));

    return window.removeEventListener('click', (e) => handleButtonClick(e));
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (showDropdownMenu) {
        setShowDropdownMenu(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [showDropdownMenu]);

  return (
    <HoverDropdownMenu.Wrapper style={style} align={align}>
      <HoverDropdownMenu.MenuItem
        data-qa="menu_item"
        onMouseEnter={showDropdown}
        onMouseLeave={hideDropdown}
        onClick={handleMobileClick}
      >
        {menuItem}
      </HoverDropdownMenu.MenuItem>
      {dropdownMenu && (
        <HoverDropdownMenu.DropdownMenu
          align={align}
          direction={direction}
          data-qa="dropdown_menu"
          isDropdownMenuPermanent={isDropdownMenuPermanent}
          onMouseEnter={handleMouseEnterDropdownMenu}
          onMouseLeave={handleMouseLeaveDropdownMenu}
          showDropdownMenu={showDropdownMenu}
        >
          {dropdownMenu}
        </HoverDropdownMenu.DropdownMenu>
      )}
    </HoverDropdownMenu.Wrapper>
  );
};

HoverDropdownMenu.Wrapper = styled.li<{ align: IHoverLinkMenu['align'] }>`
  list-style: none;
  position: ${({ align }) => (align !== 'center' ? 'relative' : 'initial')};
`;

HoverDropdownMenu.MenuItem = styled.div``;

HoverDropdownMenu.DropdownMenu = styled.div<{
  align: 'left' | 'center' | 'right';
  direction: 'top' | 'bottom';
  isDropdownMenuPermanent: boolean;
  showDropdownMenu: boolean;
}>`
  border-top: 1px solid
    ${({ theme }) =>
      theme.navigation.mainBorderColor === 'transparent' ? theme.navigation.borderColor : 'none'};
  position: absolute;

  ${({ isDropdownMenuPermanent, showDropdownMenu }) => {
    if (isDropdownMenuPermanent) {
      return css`
        visibility: visible;
      `;
    }
    if (showDropdownMenu) {
      return toggleVisible;
    }
    return css`
      opacity: 0;
      visibility: hidden;
    `;
  }}

  ${({ align }) => {
    if (align === 'center') {
      return css`
        left: 50%;
        transform: translateX(-50%);
      `;
    }

    if (align === 'right') {
      return css`
        right: 0;
      `;
    }

    return css`
      left: 0;
    `;
  }};

  ${({ align, direction }) => {
    if (direction === 'top' && align !== 'center') {
      return css`
        top: 0;
        transform: translateY(-100%);
      `;
    }
    if (direction === 'top' && align === 'center') {
      return css`
        top: 0;
        transform: translateX(-50%) translateY(-100%);
      `;
    }
  }};
`;
